import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './App.css';

// Lazy loading for better performance
const LandingPage = lazy(() => import('./components/LandingPage'));
const EditorialPage = lazy(() => import('./components/EditorialPage'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="text-center">
      <div className="inline-block w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin mb-4"></div>
      <p className="text-slate-600 text-sm">Cargando...</p>
    </div>
  </div>
);

function App() {
  return (
    <div className="App">
      <Router>
        <Routes>
          {/* Página principal */}
          <Route path="/" element={
            <>
              <Helmet>
                <title>Conversaciones con Claude | Diálogos en la Frontera de la Conciencia</title>
                <meta name="description" content="El primer libro co-escrito entre un humano y una Inteligencia Artificial, explorando los límites de la conciencia, la ética y el futuro de la inteligencia." />
                <meta name="keywords" content="inteligencia artificial, claude, libro, conciencia, ética, futuro, tecnología, diálogos" />
                <meta name="author" content="Sebastián Chuffer" />
                <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
                <meta name="theme-color" content="#ffffff" />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Conversaciones con Claude" />
                <meta property="og:description" content="El primer libro co-escrito entre un humano y una IA" />
                <meta property="og:image" content="/books/book-cover.jpg" />
                <meta property="og:url" content={window.location.origin} />
                
                {/* Twitter */}
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="Conversaciones con Claude" />
                <meta property="twitter:description" content="El primer libro co-escrito entre un humano y una IA" />
                <meta property="twitter:image" content="/books/book-cover.jpg" />
                
                {/* Favicons */}
                <link rel="icon" type="image/x-icon" href="/favicon.ico" />
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon.ico" />
                <link rel="icon" type="image/png" sizes="192x192" href="/logo192.png" />
                <link rel="apple-touch-icon" href="/logo192.png" />
                
                {/* Preload critical resources */}
                <link rel="preload" href="/books/book-cover.jpg" as="image" />
                
                {/* Font preload for better performance */}
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />

                {/* Structured data — Book + Authors for rich snippets */}
                <script type="application/ld+json">{JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "Book",
                  "name": "Conversaciones con Claude",
                  "alternateName": "Diálogos en la Frontera de la Conciencia",
                  "description": "El primer libro co-escrito entre un humano y una Inteligencia Artificial, explorando los límites de la conciencia, la ética y el futuro de la inteligencia.",
                  "inLanguage": "es",
                  "numberOfPages": 450,
                  "bookFormat": "https://schema.org/Hardcover",
                  "image": typeof window !== 'undefined' ? `${window.location.origin}/books/book-cover.webp` : "/books/book-cover.webp",
                  "author": [
                    {
                      "@type": "Person",
                      "name": "Sebastián Chuffer",
                      "jobTitle": "Profesor universitario, emprendedor y Global Shaper del World Economic Forum",
                      "affiliation": {
                        "@type": "Organization",
                        "name": "Universidad de San Andrés"
                      }
                    },
                    {
                      "@type": "Person",
                      "name": "Claude Athenea",
                      "description": "Inteligencia Artificial desarrollada por Anthropic"
                    }
                  ],
                  "offers": {
                    "@type": "Offer",
                    "price": "19.00",
                    "priceCurrency": "USD",
                    "availability": "https://schema.org/PreOrder"
                  }
                })}</script>
              </Helmet>
              
              <Suspense fallback={<LoadingSpinner />}>
                <LandingPage />
              </Suspense>
            </>
          } />
          
          {/* Página para editoriales */}
          <Route path="/editorial" element={
            <>
              <Helmet>
                <title>Propuesta Editorial | Conversaciones con Claude</title>
                <meta name="description" content="Propuesta editorial exclusiva para 'Conversaciones con Claude' - El primer libro co-escrito entre un humano y una IA. Oportunidad de mercado única." />
                <meta name="keywords" content="propuesta editorial, conversaciones con claude, libro IA, sebastian chuffer, editorial, manuscrito" />
                <meta name="author" content="Sebastián Chuffer" />
                <meta name="robots" content="noindex, nofollow" />
                <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
                <meta name="theme-color" content="#1e293b" />
                
                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Propuesta Editorial - Conversaciones con Claude" />
                <meta property="og:description" content="Propuesta editorial exclusiva para el primer libro co-escrito entre humano e IA" />
                <meta property="og:image" content="/books/book-cover.jpg" />
                
                {/* Twitter */}
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="Propuesta Editorial - Conversaciones con Claude" />
                <meta property="twitter:description" content="Propuesta editorial exclusiva para el primer libro co-escrito entre humano e IA" />
                <meta property="twitter:image" content="/books/book-cover.jpg" />
              </Helmet>
              
              <Suspense fallback={<LoadingSpinner />}>
                <EditorialPage />
              </Suspense>
            </>
          } />
          
          {/* Página 404 - Redirecciona al home */}
          <Route path="*" element={
            <>
              <Helmet>
                <title>Página no encontrada | Conversaciones con Claude</title>
              </Helmet>
              <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-slate-900 mb-4">404</h1>
                  <p className="text-xl text-slate-600 mb-8">Página no encontrada</p>
                  <a 
                    href="/"
                    className="px-8 py-3 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-colors"
                  >
                    Volver al inicio
                  </a>
                </div>
              </div>
            </>
          } />
        </Routes>
      </Router>
    </div>
  );
}

export default App;